/**
 * When variant option filters are active:
 * - Shows a loading state while all collection pages are fetched
 * - Hides pagination
 * - Renders results with infinite scroll
 */
(function () {
  const MAX_SHOPIFY_PAGES = 100;

  function hasVariantOptionFilters(url = window.location.href) {
    const params = new URLSearchParams(new URL(url, window.location.origin).search);
    if (Array.from(params.keys()).some((key) => key.startsWith('filter.v.option.'))) {
      return true;
    }

    const form = document.querySelector('#CollectionFilterForm');
    if (!form) return false;

    return Array.from(form.querySelectorAll('input:checked')).some(
      (input) => input.name && input.name.startsWith('filter.v.option.')
    );
  }

  function getAggregationCacheKey() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    return url.pathname + url.search;
  }

  function getFilterContainer() {
    return document.querySelector('filter-container[data-filter-section-id]');
  }

  function getSectionRoot(filterContainer) {
    return filterContainer.closest('.shopify-section');
  }

  function getListing(filterContainer) {
    return filterContainer.querySelector('[data-collection-listing]');
  }

  function getPaginationRow(section) {
    return section?.querySelector('[data-collection-pagination]');
  }

  function getPerPage(filterContainer) {
    const listing = getListing(filterContainer);
    return parseInt(listing?.dataset.productsPerPage, 10) || 28;
  }

  function buildBaseCollectionUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    return url;
  }

  function buildSectionFetchUrl(pageUrl, sectionId) {
    let fetchUrl = pageUrl.toString();
    if (sectionId) {
      fetchUrl += `${fetchUrl.includes('?') ? '&' : '?'}section_id=${sectionId}`;
    }
    return fetchUrl;
  }

  function parseSectionHtml(html) {
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    return template.content;
  }

  function extractProductsFromContent(content, seenIds, allProducts) {
    const blocks = content.querySelectorAll(
      '[data-collection-listing] .product-grid product-block, [data-collection-listing] product-block'
    );

    blocks.forEach((block) => {
      const productId = block.getAttribute('data-product-id');
      if (productId && !seenIds.has(productId)) {
        seenIds.add(productId);
        allProducts.push(block.outerHTML);
      }
    });
  }

  function getShopifyTotalPages(content, fallback = 1) {
    const listing = content.querySelector('[data-collection-listing]');
    const fromData = parseInt(listing?.dataset.shopifyTotalPages, 10);
    if (!Number.isNaN(fromData) && fromData > 0) {
      return fromData;
    }

    let maxPage = 1;
    content.querySelectorAll('[data-collection-pagination] .pagination__number').forEach((el) => {
      const pageNum = parseInt(el.textContent.trim(), 10);
      if (!Number.isNaN(pageNum) && pageNum > maxPage) {
        maxPage = pageNum;
      }
    });

    return maxPage || fallback;
  }

  async function fetchAllVisibleProducts(filterContainer, signal) {
    const sectionId = filterContainer.dataset.filterSectionId;
    const seenIds = new Set();
    const allProducts = [];
    const baseUrl = buildBaseCollectionUrl();

    const firstPageUrl = new URL(baseUrl.toString());
    const firstResponse = await fetch(buildSectionFetchUrl(firstPageUrl, sectionId), { signal });
    if (!firstResponse.ok) {
      return allProducts;
    }

    const firstContent = parseSectionHtml(await firstResponse.text());
    extractProductsFromContent(firstContent, seenIds, allProducts);

    const totalShopifyPages = getShopifyTotalPages(firstContent, 1);
    const pagesToFetch = Math.min(totalShopifyPages, MAX_SHOPIFY_PAGES);

    for (let page = 2; page <= pagesToFetch; page += 1) {
      const pageUrl = new URL(baseUrl.toString());
      pageUrl.searchParams.set('page', String(page));

      const response = await fetch(buildSectionFetchUrl(pageUrl, sectionId), { signal });
      if (!response.ok) break;

      const content = parseSectionHtml(await response.text());
      extractProductsFromContent(content, seenIds, allProducts);
    }

    return allProducts;
  }

  function ensureProductGrid(listing) {
    let grid = listing.querySelector('.product-grid');
    if (!grid) {
      const emptyMessage = listing.querySelector('.align-centre.fully-spaced-row');
      if (emptyMessage) emptyMessage.remove();
      grid = document.createElement('div');
      grid.className = listing.dataset.gridClass || 'product-grid collectiongrid';
      listing.appendChild(grid);
    }
    return grid;
  }

  function showLoadingState(filterContainer) {
    const listing = getListing(filterContainer);
    const section = getSectionRoot(filterContainer);
    if (!listing) return;

    listing.classList.add('collection-listing--filter-loading');

    const grid = listing.querySelector('.product-grid');
    if (grid) {
      grid.innerHTML = '';
      grid.hidden = true;
    }

    const emptyMessage = listing.querySelector('[data-collection-empty-message], .align-centre.fully-spaced-row');
    if (emptyMessage) emptyMessage.hidden = true;

    let loader = listing.querySelector('[data-collection-filter-loader]');
    if (!loader) {
      loader = document.createElement('div');
      loader.className = 'collection-filter-loader';
      loader.dataset.collectionFilterLoader = '';
      loader.innerHTML = '<div class="loading-spinner" aria-hidden="true"></div><p class="collection-filter-loader__text">Loading products...</p>';
      listing.appendChild(loader);
    }
    loader.hidden = false;

    const paginationRow = getPaginationRow(section);
    if (paginationRow) paginationRow.hidden = true;
  }

  function hideLoadingState(filterContainer) {
    const listing = getListing(filterContainer);
    if (!listing) return;

    listing.classList.remove('collection-listing--filter-loading');
    const loader = listing.querySelector('[data-collection-filter-loader]');
    if (loader) loader.hidden = true;

    const grid = listing.querySelector('.product-grid');
    if (grid) grid.hidden = false;
  }

  function removeInfiniteScrollSentinel(filterContainer) {
    const listing = getListing(filterContainer);
    listing?.querySelector('[data-collection-infinite-sentinel]')?.remove();
    listing?.querySelector('[data-collection-infinite-loader]')?.remove();
  }

  function teardownFilteredMode(filterContainer) {
    filterContainer.dataset.fullCollectionMode = 'false';
    filterContainer._allVisibleProducts = null;
    filterContainer._infiniteScrollRenderedCount = 0;

    if (filterContainer._infiniteScrollObserver) {
      filterContainer._infiniteScrollObserver.disconnect();
      filterContainer._infiniteScrollObserver = null;
    }

    const listing = getListing(filterContainer);
    if (listing) {
      listing.classList.remove('collection-listing--filter-loading');
      listing.querySelector('[data-collection-filter-loader]')?.remove();
      removeInfiniteScrollSentinel(filterContainer);
    }

    const section = getSectionRoot(filterContainer);
    const paginationRow = getPaginationRow(section);
    if (paginationRow) paginationRow.hidden = false;
  }

  function clearUrlPage() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('page')) return;
    url.searchParams.delete('page');
    window.history.replaceState({ path: url.href }, '', `${url.pathname}${url.search}`);
  }

  function renderEmptyState(filterContainer) {
    const listing = getListing(filterContainer);
    const grid = ensureProductGrid(listing);
    grid.innerHTML = '';
    grid.hidden = true;

    let emptyMessage = listing.querySelector('[data-collection-empty-message]');
    if (!emptyMessage) {
      emptyMessage = document.createElement('h5');
      emptyMessage.className = 'align-centre fully-spaced-row';
      emptyMessage.dataset.collectionEmptyMessage = '';
      listing.appendChild(emptyMessage);
    }
    emptyMessage.textContent = listing.dataset.emptyMessage || 'No products found';
    emptyMessage.hidden = false;
  }

  function appendProducts(grid, productsHtml) {
    if (!productsHtml.length) return;
    grid.insertAdjacentHTML('beforeend', productsHtml.join(''));
  }

  function ensureInfiniteScrollSentinel(filterContainer) {
    const listing = getListing(filterContainer);
    let sentinel = listing.querySelector('[data-collection-infinite-sentinel]');
    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.className = 'collection-infinite-scroll-sentinel';
      sentinel.dataset.collectionInfiniteSentinel = '';
      listing.appendChild(sentinel);
    }
    return sentinel;
  }

  function showBottomLoader(filterContainer) {
    const listing = getListing(filterContainer);
    let bottomLoader = listing.querySelector('[data-collection-infinite-loader]');
    if (!bottomLoader) {
      bottomLoader = document.createElement('div');
      bottomLoader.className = 'collection-infinite-scroll-loader';
      bottomLoader.dataset.collectionInfiniteLoader = '';
      bottomLoader.innerHTML = '<div class="loading-spinner" aria-hidden="true"></div>';
      listing.appendChild(bottomLoader);
    }
    bottomLoader.hidden = false;
  }

  function hideBottomLoader(filterContainer) {
    const listing = getListing(filterContainer);
    const bottomLoader = listing?.querySelector('[data-collection-infinite-loader]');
    if (bottomLoader) bottomLoader.hidden = true;
  }

  function loadMoreProducts(filterContainer) {
    const allProducts = filterContainer._allVisibleProducts || [];
    const renderedCount = filterContainer._infiniteScrollRenderedCount || 0;

    if (renderedCount >= allProducts.length || filterContainer._isLoadingMore) {
      return;
    }

    const perPage = getPerPage(filterContainer);
    const listing = getListing(filterContainer);
    const grid = ensureProductGrid(listing);
    const nextBatch = allProducts.slice(renderedCount, renderedCount + perPage);

    if (!nextBatch.length) return;

    filterContainer._isLoadingMore = true;
    showBottomLoader(filterContainer);

    window.requestAnimationFrame(() => {
      appendProducts(grid, nextBatch);
      filterContainer._infiniteScrollRenderedCount = renderedCount + nextBatch.length;
      filterContainer._isLoadingMore = false;
      hideBottomLoader(filterContainer);

      if (filterContainer._infiniteScrollRenderedCount >= allProducts.length) {
        if (filterContainer._infiniteScrollObserver) {
          filterContainer._infiniteScrollObserver.disconnect();
          filterContainer._infiniteScrollObserver = null;
        }
        removeInfiniteScrollSentinel(filterContainer);
      }
    });
  }

  function setupInfiniteScroll(filterContainer) {
    if (filterContainer._infiniteScrollObserver) {
      filterContainer._infiniteScrollObserver.disconnect();
    }

    const allProducts = filterContainer._allVisibleProducts || [];
    const renderedCount = filterContainer._infiniteScrollRenderedCount || 0;

    if (renderedCount >= allProducts.length) {
      removeInfiniteScrollSentinel(filterContainer);
      return;
    }

    const sentinel = ensureInfiniteScrollSentinel(filterContainer);
    filterContainer._infiniteScrollObserver = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        loadMoreProducts(filterContainer);
      }
    }, { rootMargin: '300px' });

    filterContainer._infiniteScrollObserver.observe(sentinel);
  }

  function renderInitialResults(filterContainer) {
    const allProducts = filterContainer._allVisibleProducts || [];
    const listing = getListing(filterContainer);
    const perPage = getPerPage(filterContainer);

    if (!allProducts.length) {
      renderEmptyState(filterContainer);
      removeInfiniteScrollSentinel(filterContainer);
      return;
    }

    const grid = ensureProductGrid(listing);
    const initialBatch = allProducts.slice(0, perPage);
    grid.innerHTML = initialBatch.join('');
    grid.hidden = false;

    const emptyMessage = listing.querySelector('[data-collection-empty-message]');
    if (emptyMessage) emptyMessage.hidden = true;

    filterContainer._infiniteScrollRenderedCount = initialBatch.length;
    setupInfiniteScroll(filterContainer);
  }

  async function aggregateAndRenderInfinite(filterContainer) {
    if (!hasVariantOptionFilters()) {
      teardownFilteredMode(filterContainer);
      return;
    }

    const listing = getListing(filterContainer);
    if (!listing) return;

    if (filterContainer._aggregationAbortController) {
      filterContainer._aggregationAbortController.abort();
    }
    filterContainer._aggregationAbortController = new AbortController();
    const { signal } = filterContainer._aggregationAbortController;

    filterContainer.dataset.fullCollectionMode = 'true';
    filterContainer._isAggregating = true;
    filterContainer._infiniteScrollRenderedCount = 0;

    showLoadingState(filterContainer);
    clearUrlPage();

    try {
      const allProducts = await fetchAllVisibleProducts(filterContainer, signal);
      filterContainer._allVisibleProducts = allProducts;
      filterContainer._aggregationCacheKey = getAggregationCacheKey();

      hideLoadingState(filterContainer);
      renderInitialResults(filterContainer);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Collection aggregation failed:', error);
        hideLoadingState(filterContainer);
      }
    } finally {
      filterContainer._isAggregating = false;
    }
  }

  function init() {
    const filterContainer = getFilterContainer();
    if (!filterContainer) return;

    const listing = getListing(filterContainer);
    if (!listing) return;

    const section = getSectionRoot(filterContainer);

    section.addEventListener('click', (event) => {
      if (!hasVariantOptionFilters() || filterContainer.dataset.fullCollectionMode !== 'true') return;
      const shopifyPaginationLink = event.target.closest('[data-collection-pagination] .pagination a');
      if (shopifyPaginationLink) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);

    document.addEventListener('change', (event) => {
      const input = event.target.closest('#CollectionFilterForm input');
      if (!input || !input.name || !input.name.startsWith('filter.v.option.')) return;
      if (input.checked || hasVariantOptionFilters()) {
        showLoadingState(filterContainer);
      }
    }, true);

    filterContainer.addEventListener('on:collection:ajax-updated', () => {
      if (!hasVariantOptionFilters()) {
        teardownFilteredMode(filterContainer);
        return;
      }
      showLoadingState(filterContainer);
      aggregateAndRenderInfinite(filterContainer);
    });

    window.addEventListener('popstate', () => {
      if (!hasVariantOptionFilters()) {
        teardownFilteredMode(filterContainer);
        return;
      }
      const cacheKey = getAggregationCacheKey();
      if (filterContainer._aggregationCacheKey !== cacheKey) {
        showLoadingState(filterContainer);
        aggregateAndRenderInfinite(filterContainer);
      }
    });

    if (hasVariantOptionFilters()) {
      showLoadingState(filterContainer);
      aggregateAndRenderInfinite(filterContainer);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
